import { isEqual } from 'lodash';

import { getAttributeValueKey } from 'companySettings/components/PartFinderAttributeValues/utils';
import { AttributeType } from 'companySettings/enums';
import {
  IPartFinderAttributeValue,
  IPartFinderFamily,
  IPartFinderFamilyAttribute,
  IPartFinderFamilyChanges,
  IPartFinderFamilyPart,
  IPartFinderFamilyReference,
} from 'companySettings/types';

import { AttributeChangeState, IFamilyPartForm, IUpsertPartFinderFamilyFormData } from './types';

// An attribute's key in the form: its id once saved. Until then a family-specific attribute has a
// client key of its own - a rename or a second attribute from the same value list must not move
// its part values - and an inherited one its source, which is the family's only once.
export const getAttributeKey = (attribute: IPartFinderFamilyAttribute) => {
  if (attribute.id) {
    return `id-${attribute.id}`;
  }

  return (
    attribute.clientKey ?? `${attribute.sourceLevel ?? 'typed'}-${attribute.sourceAttributeId}`
  );
};

// One part whatever the spelling, as the catalogue compares part numbers ('_' and ' ' are one).
export const getPartKey = (part: Pick<IFamilyPartForm, 'partNumber' | 'manufacturerId'>) =>
  `${(part.partNumber ?? '').trim().toLowerCase().replace(/_/g, ' ')}|${part.manufacturerId}`;

export const mapPartFromServer = (part: IPartFinderFamilyPart): IFamilyPartForm => ({
  ...part,
  values: Object.fromEntries(
    (part.values ?? []).map(({ attributeId, values }) => [`id-${attributeId}`, values])
  ),
});

// The numbers a Number & Range attribute offers a part: every step of a range that has an
// increment, a range's ends otherwise, and each single number.
export const getRangeOptions = (values: IPartFinderAttributeValue[]) => {
  const numbers = new Set<number>();

  (values ?? []).forEach(({ rangeMin, rangeMax, rangeIncrement }) => {
    const min = Number(rangeMin);
    const max = Number(rangeMax);
    const step = Number(rangeIncrement);

    if (!Number.isFinite(min)) {
      return;
    }

    numbers.add(min);

    if (!rangeMax || !Number.isFinite(max)) {
      return;
    }

    if (step > 0) {
      for (let current = min; current <= max && numbers.size < 500; current += step) {
        numbers.add(Math.round(current * 10000) / 10000);
      }
    }

    numbers.add(max);
  });

  return [...numbers].sort((a, b) => a - b).map(String);
};

export const isRangeAttribute = (attribute: IPartFinderFamilyAttribute) =>
  AttributeType.NUMBER_RANGE === attribute.type;

// How an attribute in the form stands against the saved family: not saved yet, saved with other
// content (name, values, use, group or settings), or as saved.
export const getAttributeChangeState = (
  attribute: IPartFinderFamilyAttribute,
  savedByKey: Map<string, IPartFinderFamilyAttribute>
): AttributeChangeState | null => {
  const saved = savedByKey.get(getAttributeKey(attribute));

  if (!saved) {
    return AttributeChangeState.NEW;
  }

  return isEqual(normalizeAttributes([attribute]), normalizeAttributes([saved]))
    ? null
    : AttributeChangeState.CHANGED;
};

// Whether a part's values for one attribute differ from the saved part's.
export const isPartValueChanged = (
  part: Pick<IFamilyPartForm, 'values'>,
  loaded: Pick<IFamilyPartForm, 'values'>,
  attributeKey: string
) => !isEqual(part.values?.[attributeKey] ?? [], loaded.values?.[attributeKey] ?? []);

// What a save would send, so two attribute lists compare by content and order only.
const normalizeAttributes = (attributes: IPartFinderFamilyAttribute[]) =>
  (attributes ?? []).map((attribute) => ({
    id: attribute.id ?? null,
    group: attribute.group ?? null,
    inUse: Boolean(attribute.inUse),
    name: (attribute.name ?? '').trim(),
    type: attribute.type,
    sourceAttributeId: attribute.sourceAttributeId ?? null,
    sourceAttributeVersionId: attribute.sourceAttributeVersionId ?? null,
    sourceVersionNumber: attribute.sourceVersionNumber ?? null,
    updateList: Boolean(attribute.updateList),
    values: (attribute.values ?? []).map((value) => ({
      value: value.value ?? null,
      colorHex: value.colorHex ?? null,
      rangeMin: value.rangeMin ?? null,
      rangeMax: value.rangeMax ?? null,
      rangeIncrement: value.rangeIncrement ?? null,
      rangeUnit: value.rangeUnit ?? null,
    })),
  }));

// Parts compare by identity, order and values only.
const normalizeParts = (parts: IFamilyPartForm[]) =>
  (parts ?? []).map((part) => ({ key: getPartKey(part), values: part.values ?? {} }));

// A new family always has something to save; a saved one only once its name, product type,
// category, attributes, their values or their order differ from what was loaded - as a product
// type's Save as Draft.
export const hasFamilyChanges = (
  data: IUpsertPartFinderFamilyFormData,
  family?: IPartFinderFamily,
  loadedParts: IFamilyPartForm[] = []
) =>
  !family ||
  (data.name ?? '').trim() !== family.name ||
  data.productTypeId !== family.productTypeId ||
  data.category?.id !== family.categoryId ||
  !isEqual(normalizeAttributes(data.attributes), normalizeAttributes(family.attributes)) ||
  !isEqual(normalizeParts(data.parts), normalizeParts(loadedParts));

// What the editor would save, in the shape a version's summary has, so it reads as the history
// does: the details, the attributes by key (added, removed, renamed, otherwise changed, taken into
// or out of use, regrouped, reordered) and the parts (added, removed, other values).
export const getFamilyUnsavedChanges = (
  data: IUpsertPartFinderFamilyFormData,
  family: IPartFinderFamily,
  loadedParts: IFamilyPartForm[] = [],
  productTypes: IPartFinderFamilyReference[] = []
): IPartFinderFamilyChanges => {
  const changes: IPartFinderFamilyChanges = {};
  const name = (data.name ?? '').trim();

  if (name !== family.name) {
    changes.name = { from: family.name, to: name };
  }

  if (data.productTypeId !== family.productTypeId) {
    const toProductType = productTypes.find(({ id }) => id === data.productTypeId);

    changes.productType = {
      from: family.productTypeId
        ? { id: family.productTypeId, name: family.productTypeName ?? '' }
        : null,
      to: toProductType ? { id: toProductType.id, name: toProductType.name } : null,
    };
  }

  if (data.category?.id !== family.categoryId) {
    changes.category = {
      from: { id: family.categoryId, name: family.categoryName ?? '' },
      to: data.category ? { id: data.category.id, name: data.category.name } : null,
    };
  }

  const before = new Map((family.attributes ?? []).map((item) => [getAttributeKey(item), item]));
  const after = new Map((data.attributes ?? []).map((item) => [getAttributeKey(item), item]));
  const attributes: IPartFinderFamilyChanges['attributes'] = {
    added: [],
    removed: [],
    renamed: [],
    changed: [],
    used: [],
    unused: [],
    regrouped: [],
  };

  after.forEach((attribute, key) => {
    const previous = before.get(key);
    const attributeName = (attribute.name ?? '').trim();

    if (!previous) {
      attributes.added.push({
        name: attributeName,
        type: attribute.type,
        values: attribute.values ?? [],
      });

      return;
    }

    const [normalized] = normalizeAttributes([attribute]);
    const [previousNormalized] = normalizeAttributes([previous]);

    if (normalized.name !== previousNormalized.name) {
      attributes.renamed.push({ from: previousNormalized.name, to: normalized.name });
    }

    if (normalized.inUse !== previousNormalized.inUse) {
      attributes[normalized.inUse ? 'used' : 'unused'].push({ name: attributeName });
    }

    if (normalized.group !== previousNormalized.group) {
      attributes.regrouped.push({
        name: attributeName,
        from: previousNormalized.group,
        to: normalized.group,
      });
    }

    const settings = { ...normalized, name: '', inUse: false, group: null };
    const previousSettings = { ...previousNormalized, name: '', inUse: false, group: null };

    if (!isEqual(settings, previousSettings)) {
      attributes.changed.push({ name: attributeName });
    }
  });

  before.forEach((attribute, key) => {
    if (!after.has(key)) {
      attributes.removed.push({ name: (attribute.name ?? '').trim() });
    }
  });

  const keptKeys = [...after.keys()].filter((key) => before.has(key));
  const previousKeptKeys = [...before.keys()].filter((key) => after.has(key));

  attributes.reordered = !isEqual(keptKeys, previousKeptKeys);

  const changedAttributes = Object.fromEntries(
    Object.entries(attributes).filter(([, value]) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    )
  );

  if (Object.keys(changedAttributes).length) {
    changes.attributes = changedAttributes;
  }

  const loadedByKey = new Map((loadedParts ?? []).map((part) => [getPartKey(part), part]));
  const partKeys = new Set((data.parts ?? []).map(getPartKey));
  const added = (data.parts ?? []).filter((part) => !loadedByKey.has(getPartKey(part)));
  const removed = (loadedParts ?? []).filter((part) => !partKeys.has(getPartKey(part)));
  const valuesChanged = (data.parts ?? []).filter((part) => {
    const loaded = loadedByKey.get(getPartKey(part));

    return loaded && !isEqual(part.values ?? {}, loaded.values ?? {});
  }).length;

  if (added.length || removed.length || valuesChanged) {
    changes.parts = {
      ...(added.length && { added: added.map((part) => part.partNumber) }),
      ...(removed.length && { removed: removed.map((part) => part.partNumber) }),
      ...(valuesChanged && { valuesChanged }),
    };
  }

  return changes;
};

// A number a Number & Range attribute takes: within one of its ranges, or one of its numbers.
const isInRange = (attribute: IPartFinderFamilyAttribute, numberValue: string | number) =>
  (attribute.values ?? []).some(({ rangeMin, rangeMax }) => {
    const value = Number(numberValue);

    return rangeMax
      ? value >= Number(rangeMin) && value <= Number(rangeMax)
      : value === Number(rangeMin);
  });

// A part keeps only the values its attributes still have: one unticked, removed or edited away
// goes from every part, as the server refuses a value the attribute does not have.
export const prunePartValues = (
  parts: IFamilyPartForm[],
  attributes: IPartFinderFamilyAttribute[]
): IFamilyPartForm[] => {
  const attributesByKey = new Map(
    attributes.map((attribute) => [getAttributeKey(attribute), attribute])
  );

  return (parts ?? []).map((part) => {
    let isPruned = false;

    const values = Object.fromEntries(
      Object.entries(part.values ?? {}).map(([attributeKey, partValues]) => {
        const attribute = attributesByKey.get(attributeKey);

        if (!attribute) {
          return [attributeKey, partValues];
        }

        const valueKeys = new Set(
          attribute.values.map((value) => getAttributeValueKey(attribute.type, value))
        );
        const kept = (partValues ?? []).filter((partValue) =>
          isRangeAttribute(attribute)
            ? isInRange(attribute, partValue.numberValue)
            : valueKeys.has(getAttributeValueKey(attribute.type, partValue))
        );

        isPruned = isPruned || kept.length !== (partValues ?? []).length;

        return [attributeKey, kept];
      })
    );

    return isPruned ? { ...part, values } : part;
  });
};

// The part values of attributes whose key changed (an attribute converted before it was saved)
// follow them to the new key.
export const movePartValueKeys = (
  parts: IFamilyPartForm[],
  movedKeys: Record<string, string>
): IFamilyPartForm[] =>
  Object.keys(movedKeys).length
    ? (parts ?? []).map((part) => ({
        ...part,
        values: Object.fromEntries(
          Object.entries(part.values ?? {}).map(([key, values]) => [movedKeys[key] ?? key, values])
        ),
      }))
    : parts ?? [];
